import type { ComponentType } from "react";
import WorkHoursClientView from "./sections/WorkHoursClientView";
import TeamClientView from "./sections/TeamClientView";
import ExpensesClientView from "./sections/ExpensesClientView";
import CrmClientView from "./sections/CrmClientView";

// ---------------------------------------------------------------------------
// Vista del cliente desde CC-CONSOLE (CC-CONSOLE #981 / épica #983).
// Las MISMAS apps internas que Coding Cloud consulta en CC-CONSOLE (con todos
// sus clientes), abiertas aquí muestran solo lo de este cliente.
// Viven bajo `/coding-cloud/...`: las rutas `/work-hours`, `/team` y `/expenses`
// son ahora de las apps completas de Gestión de Staff y Finanzas internas
// (portadas de CC-CONSOLE), que guardan los datos propios de esta plataforma.
// Única fuente: el menú de apps (appsConfig.tsx), el sub-menú y las rutas.
// ---------------------------------------------------------------------------

export interface ClientViewDef {
  /** Segmento base de la ruta (`/coding-cloud/work-hours`). */
  base: string;
  /** Sección por defecto bajo la base (`dashboard`). */
  item: string;
  /** Categoría y app del menú de apps (appsConfig / wayak-config.json). */
  categoryId: "gestion-staff" | "finanzas" | "crm";
  appId: string;
  /** Claves i18n (`common`). */
  labelKey: string;
  descriptionKey: string;
  iconClass: string;
  color: string;
  Section: ComponentType;
  /** Roles que la ven además de la administración de la plataforma. */
  extraRoleIds?: string[];
}

/**
 * Equipo comercial: roles que ven el CRM leído de CC-CONSOLE. Misma lista que
 * `CC_TENANT_VIEW_CRM_ROLE_IDS` en el backend, que es quien de verdad decide.
 */
export const CRM_VIEW_ROLE_IDS: string[] = String(import.meta.env.VITE_CC_TENANT_VIEW_CRM_ROLE_IDS ?? "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

export const CLIENT_VIEWS: ClientViewDef[] = [
  {
    base: "/coding-cloud/work-hours",
    item: "dashboard",
    categoryId: "gestion-staff",
    appId: "gestion-staff-horas",
    labelKey: "clientView.workHours.title",
    descriptionKey: "clientView.workHours.app",
    iconClass: "ri-time-line",
    color: "bg-violet-600",
    Section: WorkHoursClientView,
  },
  {
    base: "/coding-cloud/team",
    item: "summary",
    categoryId: "gestion-staff",
    appId: "gestion-staff-equipo",
    labelKey: "clientView.team.title",
    descriptionKey: "clientView.team.app",
    iconClass: "ri-team-line",
    color: "bg-sky-600",
    Section: TeamClientView,
  },
  {
    base: "/coding-cloud/expenses",
    item: "summary",
    categoryId: "finanzas",
    appId: "finanzas-gastos",
    labelKey: "clientView.expenses.title",
    descriptionKey: "clientView.expenses.app",
    iconClass: "ri-money-dollar-circle-line",
    color: "bg-teal-600",
    Section: ExpensesClientView,
  },
  {
    base: "/coding-cloud/crm",
    item: "prospectos",
    categoryId: "crm",
    appId: "crm-prospectos-coding-cloud",
    labelKey: "clientView.crm.title",
    descriptionKey: "clientView.crm.app",
    iconClass: "ri-kanban-view",
    color: "bg-indigo-600",
    Section: CrmClientView,
    extraRoleIds: CRM_VIEW_ROLE_IDS,
  },
];

export const clientViewRoute = (v: ClientViewDef) => `${v.base}/${v.item}`;
